import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Link,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useLocation, useNavigate } from 'react-router-dom';

import { Carrier, ImportFile, endpoints, requireField, sbcParcelApi } from '../api/sbcparcel.api';
import { useCustomer } from '../customer';

const CARRIER_LABELS: Record<Carrier, string> = {
  ups: 'UPS',
  gls: 'GLS',
  fbo: 'FedEx FBO',
  dhl: 'DHL',
};

const UPLOAD_ENDPOINTS: Record<Carrier, string> = {
  ups: endpoints.uploadUPSImport,
  gls: endpoints.uploadGLSImport,
  fbo: endpoints.uploadFBOImport,
  dhl: endpoints.uploadDHLImport,
};

const MAX_FILES_PER_UPLOAD = 25;
const formatSize = (bytes: number): string =>
  bytes >= 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;

const formatWhen = (iso: string): string => {
  if (!iso) return 'Unknown';
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return 'Unknown';
  const sameDay = new Date().toDateString() === when.toDateString();
  const time = when.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return sameDay ? `Today, ${time}` : `${when.toLocaleDateString()}, ${time}`;
};

type Batch = {
  key: string;
  label: string;
  meta: string;
  items: ImportFile[];
};

/**
 * Files are grouped by the batch id stamped on upload. Blobs predating that carry none and
 * fall back to their upload minute — one old batch lands over several seconds, so grouping
 * those on the exact timestamp would show a single upload as a run of one-file groups.
 */
const groupIntoBatches = (files: ImportFile[]): Batch[] => {
  const order: string[] = [];
  const byKey: Record<string, ImportFile[]> = {};

  files.forEach((file) => {
    const key = file.batch || `t:${file.uploadedAt.slice(0, 16)}`;
    if (!byKey[key]) {
      byKey[key] = [];
      order.push(key);
    }
    byKey[key].push(file);
  });

  return order.map((key, index) => {
    const items = byKey[key];
    const bytes = items.reduce((sum, f) => sum + f.size, 0);
    return {
      key,
      label: `Upload ${index + 1} · ${formatWhen(items[0].uploadedAt)}`,
      meta: `— ${items.length} file${items.length === 1 ? '' : 's'}, ${formatSize(bytes)}`,
      items,
    };
  });
};

export type ImportFilesPageProps = {
  carrier: Carrier;
};

export const ImportFilesPage: React.FC<ImportFilesPageProps> = ({ carrier }) => {
  const { customer } = useCustomer();
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const week = new URLSearchParams(location.search).get('week') || '';
  const carrierLabel = CARRIER_LABELS[carrier];

  const [files, setFiles] = React.useState<ImportFile[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [uploading, setUploading] = React.useState(0);
  const [busyName, setBusyName] = React.useState<string | null>(null);
  const [confirmingClear, setConfirmingClear] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      setFiles(requireField<{ files: ImportFile[] }>(await sbcParcelApi.getImportFiles(customer.id, week, carrier), 'files').files);
      setError(null);
    } catch (err: any) {
      setError(err?.message || 'Could not load the files for this week.');
    } finally {
      setLoading(false);
    }
  }, [customer.id, week, carrier]);

  React.useEffect(() => {
    if (week) load();
  }, [load, week]);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(event.target.files ?? []);
    // Reset first so picking the same file again still fires a change event.
    event.target.value = '';
    if (picked.length === 0) return;

    if (picked.length > MAX_FILES_PER_UPLOAD) {
      setError(
        `Select at most ${MAX_FILES_PER_UPLOAD} files at a time. Add the rest as another upload.`
      );
      return;
    }

    setUploading(picked.length);
    setError(null);
    try {
      const formData = new FormData();
      picked.forEach((file) => formData.append('files', file));
      formData.append('week', week);
      formData.append('customer', customer.id);

      const response = await fetch(UPLOAD_ENDPOINTS[carrier], { method: 'POST', body: formData });
      const body = await response.json().catch(() => null);
      if (!response.ok) throw new Error(body?.message || 'Upload failed.');
      setFiles(body?.files ?? []);
    } catch (err: any) {
      setError(err?.message || 'Upload failed.');
      // The request may have landed partially; show what is actually there.
      load();
    } finally {
      setUploading(0);
    }
  };

  const removeOne = async (name: string) => {
    setBusyName(name);
    setError(null);
    try {
      setFiles(requireField<{ files: ImportFile[] }>(await sbcParcelApi.removeImportFiles(customer.id, week, carrier, name), 'files').files);
    } catch (err: any) {
      setError(err?.message || `Could not remove ${name}.`);
    } finally {
      setBusyName(null);
    }
  };

  const removeAll = async () => {
    setConfirmingClear(false);
    setBusyName('*');
    setError(null);
    try {
      setFiles(requireField<{ files: ImportFile[] }>(await sbcParcelApi.removeImportFiles(customer.id, week, carrier), 'files').files);
    } catch (err: any) {
      setError(err?.message || 'Could not remove the files.');
    } finally {
      setBusyName(null);
    }
  };

  const batches = groupIntoBatches(files);
  const totalBytes = files.reduce((sum, f) => sum + f.size, 0);
  const busy = uploading > 0 || busyName !== null;

  if (!week) {
    return (
      <Box sx={{ maxWidth: 1000, mx: 'auto', p: 3 }}>
        <Alert severity="error">No week was given. Open this page from a week card.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: 3, display: 'flex', flexDirection: 'column', gap: 2.25 }}>
      <Link
        component="button"
        type="button"
        onClick={() => navigate('/')}
        sx={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 0.75, fontSize: 13, fontWeight: 500 }}
      >
        <ArrowBackIcon sx={{ fontSize: 16 }} />
        Back to Parcel Invoice Management
      </Link>

      <Box>
        <Typography variant="h5" sx={{ mb: 0.75 }}>
          Import Files
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {customer.name} &middot; week of <b>{week}</b> &middot; {carrierLabel}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
            {loading
              ? 'Loading…'
              : files.length === 0
                ? 'No files'
                : `${files.length} file${files.length === 1 ? '' : 's'} uploaded`}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
            {loading
              ? ' '
              : files.length === 0
                ? 'Nothing will be processed on the next run'
                : `${formatSize(totalBytes)} total · ${batches.length} upload${batches.length === 1 ? '' : 's'}`}
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => fileInputRef.current?.click()}
          disabled={busy || loading}
        >
          Add Files
        </Button>
        <input type="file" ref={fileInputRef} style={{ display: 'none' }} multiple onChange={handleFileChange} />

        <Button
          variant="outlined"
          color="error"
          onClick={() => setConfirmingClear(true)}
          disabled={busy || loading || files.length === 0}
        >
          Clear All
        </Button>
      </Paper>

      {uploading > 0 && (
        <Paper variant="outlined" sx={{ p: 2, borderColor: 'primary.light' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.dark' }}>
              Uploading {uploading} file{uploading === 1 ? '' : 's'}&hellip;
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Do not close this page
            </Typography>
          </Stack>
          <LinearProgress />
        </Paper>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : files.length > 0 ? (
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ px: 2.25, py: 1.25, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}
          >
            <Typography variant="caption" sx={{ flex: 1, fontWeight: 500, color: 'text.secondary', letterSpacing: 0.5 }}>
              FILE
            </Typography>
            <Typography variant="caption" sx={{ width: 88, textAlign: 'right', fontWeight: 500, color: 'text.secondary', letterSpacing: 0.5 }}>
              SIZE
            </Typography>
            <Box sx={{ width: 40 }} />
          </Stack>

          {batches.map((batch) => (
            <Box key={batch.key}>
              <Stack
                direction="row"
                flexWrap="wrap"
                alignItems="center"
                spacing={1}
                sx={{ px: 2.25, py: 1.25, bgcolor: '#f3f6fb', borderBottom: '1px solid', borderColor: 'divider' }}
              >
                <UploadFileIcon sx={{ fontSize: 16, color: 'primary.dark' }} />
                <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.dark' }}>
                  {batch.label}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {batch.meta}
                </Typography>
              </Stack>

              {batch.items.map((file) => (
                <Stack
                  key={file.name}
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  sx={{ px: 2.25, py: 1.25, borderBottom: '1px solid', borderColor: 'grey.100' }}
                >
                  <Typography variant="body2" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {file.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ width: 88, textAlign: 'right' }}>
                    {formatSize(file.size)}
                  </Typography>
                  <Tooltip title="Remove this file">
                    <span>
                      <IconButton size="small" onClick={() => removeOne(file.name)} disabled={busy} aria-label={`Remove ${file.name}`}>
                        {busyName === file.name ? <CircularProgress size={16} /> : <CloseIcon sx={{ fontSize: 17 }} />}
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              ))}
            </Box>
          ))}
        </Paper>
      ) : (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', borderStyle: 'dashed' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 0.75 }}>
            No files uploaded for this week
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Nothing to process. The week can still be rerun once files are added.
          </Typography>
        </Paper>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
        Up to <b>{MAX_FILES_PER_UPLOAD} files per upload</b>, 50 MB each &mdash; add more in another
        batch if you have a bigger set. Uploading a file that is already listed replaces it.
        Everything listed here is what the next run will read.
      </Typography>

      <Dialog open={confirmingClear} onClose={() => setConfirmingClear(false)}>
        <DialogTitle>Remove all uploaded files?</DialogTitle>
        <DialogContent>
          <Alert severity="error" icon={false} sx={{ mb: 2 }}>
            <Typography sx={{ fontWeight: 700 }}>{customer.name}</Typography>
            <Typography variant="body2">
              {files.length} file{files.length === 1 ? '' : 's'} for the week of {week} &middot;{' '}
              {formatSize(totalBytes)}
            </Typography>
          </Alert>
          <Typography variant="body2">
            They will have to be uploaded again before this week can be processed. This cannot be
            undone.
          </Typography>
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button onClick={() => setConfirmingClear(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={removeAll} variant="contained" color="error">
            Remove all files
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ImportFilesPage;
